import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import authService from '../../services/authService';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verified = searchParams.get('verified');
  const reason = searchParams.get('reason');
  const emailFromSignup = searchParams.get('email');

  const [email, setEmail] = useState(emailFromSignup || '');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);
  const [resendError, setResendError] = useState(null);

  const handleResend = async (e) => {
    e?.preventDefault();
    const targetEmail = email?.trim();
    if (!targetEmail) {
      setResendError('يرجى إدخال البريد الإلكتروني');
      return;
    }
    setResendLoading(true);
    setResendError(null);
    setResendMessage(null);
    try {
      const { data } = await authService.resendVerification(targetEmail);
      setResendMessage(data?.message || 'تم إرسال رابط التحقق');
    } catch (err) {
      setResendError(err.response?.data?.message || 'فشل إرسال الرابط. يرجى المحاولة لاحقاً.');
    } finally {
      setResendLoading(false);
    }
  };

  const getContent = () => {
    if (verified === 'success') {
      return {
        title: 'تم التحقق بنجاح',
        message: 'تم تأكيد بريدك الإلكتروني. يمكنك الآن تسجيل الدخول.',
        icon: 'CheckCircle',
        variant: 'success',
      };
    }
    if (verified === 'error') {
      const isExpired = reason === 'expired';
      return {
        title: isExpired ? 'انتهت صلاحية الرابط' : 'رابط غير صالح',
        message: isExpired
          ? 'انتهت صلاحية رابط التحقق. يرجى طلب رابط جديد.'
          : 'الرابط غير صالح أو تم استخدامه مسبقاً.',
        icon: 'AlertCircle',
        variant: 'warning',
      };
    }
    // User came from signup - show check your email
    return {
      title: 'تحقق من بريدك الإلكتروني',
      message: emailFromSignup
        ? `تم إرسال رابط التحقق إلى ${emailFromSignup}. يرجى النقر على الرابط لتأكيد حسابك.`
        : 'تم إرسال رابط التحقق إلى بريدك الإلكتروني. يرجى النقر على الرابط لتأكيد حسابك.',
      icon: 'Mail',
      variant: 'default',
    };
  };

  const content = getContent();

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <Header />
      <main className="pt-16 lg:pt-20">
        <section className="py-12 md:py-16 lg:py-20">
          <div className="max-w-2xl mx-auto px-4 md:px-6 lg:px-8">
            <button
              onClick={() => navigate('/signup')}
              className="flex items-center gap-2 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] mb-8 transition-colors"
            >
              <Icon name="ArrowRight" size={20} />
              <span>عودة</span>
            </button>

            <div className="bg-[var(--color-card)] rounded-2xl p-8 md:p-10 shadow-warm">
              <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center ${
                      content.variant === 'success'
                        ? 'bg-green-100 text-green-600'
                        : content.variant === 'warning'
                          ? 'bg-amber-100 text-amber-600'
                          : 'bg-[var(--color-accent)]/20 text-[var(--color-accent)]'
                    }`}
                  >
                    <Icon name={content.icon} size={32} color="currentColor" />
                  </div>
                </div>
                <h2
                  className="text-2xl md:text-3xl font-heading font-bold text-[var(--color-foreground)] mb-2"
                  style={{ fontFamily: 'Aref Ruqaa, serif' }}
                >
                  {content.title}
                </h2>
                <p className="text-[var(--color-muted-foreground)] text-base md:text-lg">
                  {content.message}
                </p>
              </div>

              {(verified === 'error' || !verified) && (
                <form onSubmit={handleResend} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2 text-right">
                      البريد الإلكتروني
                    </label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="أدخِل بريدك الإلكتروني"
                      disabled={!!emailFromSignup}
                    />
                  </div>
                  {resendMessage && (
                    <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm text-center">
                      {resendMessage}
                    </div>
                  )}
                  {resendError && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm text-center">
                      {resendError}
                    </div>
                  )}
                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    disabled={resendLoading}
                    className="w-full rounded-full py-4 px-8 text-lg font-semibold"
                  >
                    {resendLoading ? 'جاري الإرسال...' : 'إعادة إرسال رابط التحقق'}
                  </Button>
                </form>
              )}

              <div className="mt-8 text-center">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={() =>
                    navigate('/signup', {
                      state: verified === 'success' ? { openLogin: true } : undefined,
                    })
                  }
                  className="rounded-full"
                >
                  {verified === 'success' ? 'تسجيل الدخول' : 'العودة لتسجيل الدخول'}
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default VerifyEmail;
